import React, { useState, useEffect, useRef } from 'react';
import Masonry from 'react-masonry-css';
import AppLayout from '../components/AppLayout';
import SocialConnectPanel from '../components/SocialConnectPanel';
import { useSocialConnect } from '../hooks/useSocialConnect';
import { GmailDetailModal } from '../components/GmailDetailModal';
import DMCoachModal from '../components/DMCoachModal';
import TweetCoachModal from '../components/TweetCoachModal';
import Modal from '../components/Modal';
import SendDMPicker from '../components/SendDMPicker';
import { fetchDmTargets, DMTargets } from '../services/dmTargetsAPI';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useResponsive } from '../hooks/useResponsive';
import { integrationsAPI, XDMThread } from '../services/integrationsAPI';
import { youtubeUploadService, YouTubeVideoFile, YouTubeUploadProgress } from '../services/youtubeUploadService';
import { familyAPI, athleteAPI } from '../services/api';
import { BACKEND_CONFIG } from '../config/backendConfig';
import {
  Grid, Mail, PlayCircle,
  ChevronUp, ChevronDown, BarChart2, Eye, Heart,
  MessageCircle, Share, ThumbsUp, TrendingUp, Activity,
  Calendar, Paperclip, Repeat, Video, X, Folder, ChevronDown as ChevronDownIcon, Share2, Image, FileText, Filter, ArrowRight
} from 'react-feather';
import { XIcon } from '../components/XIcon';
import { TextInput, TextArea, Select } from '../components/ui';

interface GmailMessage {
  message_id: string;
  thread_id: string;
  to_emails: string[];
  subject: string;
  timestamp: string;
  tracking_token?: string;
  is_opened: boolean;
  opened_at?: string;
  // Enhanced thread data
  threadData?: any;
  threadReplies?: any;
  unreadCount?: number;
  // Recipients with coach/school data
  recipients?: Array<{
    email: string;
    coach_id?: string;
    school_id?: number;
    coach_name?: string;
    school_name?: string;
  }>;
}

// Filter option for school/coach dropdown
interface SchoolCoachFilter {
  schoolId: number;
  coachId: string;
  label: string; // "School Name | Coach Name"
}

interface ThreadData {
  threadId: string;
  unreadCount: number;
  threadDetails: any;
}

interface SocialPost {
  id: string;
  provider_post_id?: string;
  platform: 'youtube' | 'twitter' | 'x' | 'gmail';
  type: 'video' | 'tweet' | 'post' | 'email';
  title?: string;
  text?: string;
  thumbnail?: string;
  media_urls?: string[];
  url?: string;
  date: string;
  metrics?: {
    views?: number;
    likes?: number;
    comments?: number;
    shares?: number;
    retweets?: number;
  };
  // Gmail-specific fields
  emailData?: {
    from?: string;
    to?: string[];
    subject?: string;
    snippet?: string;
    threadId?: string;
    messageId?: string;
    isRead?: boolean;
    attachments?: Array<{
      filename: string;
      url: string;
      mimeType?: string;
      size?: number;
    }>;
  };
  // Store full Gmail message data for detail modal
  gmailMessage?: GmailMessage;
  // Recipients with coach/school info (for Gmail and X)
  recipients?: Array<{
    email?: string;
    coach_id?: string;
    school_id?: number;
    coach_name?: string;
    school_name?: string;
  }>;
}

const Messages: React.FC = () => {
  const { sessionToken } = useAuth();
  const toast = useToast();
  const { isMobile, isTablet } = useResponsive();
  const [athleteId, setAthleteId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [socialPosts, setSocialPosts] = useState<SocialPost[]>([]);
  // APS-259: /messages?filter=youtube (the game plan's "Upload one" link) opens on that filter.
  const [activeFilter, setActiveFilter] = useState<'all' | 'gmail' | 'twitter' | 'youtube' | 'dms'>(() => {
    try {
      const f = new URLSearchParams(window.location.search).get('filter');
      return f === 'gmail' || f === 'twitter' || f === 'youtube' || f === 'dms' ? f : 'all';
    } catch {
      return 'all';
    }
  });
  const [schoolCoachFilter, setSchoolCoachFilter] = useState<SchoolCoachFilter | null>(null);
  const [schoolCoachOptions, setSchoolCoachOptions] = useState<SchoolCoachFilter[]>([]);
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  // How many sent emails to request. Grows by 20 via "Load more emails".
  const [gmailLimit, setGmailLimit] = useState(20);
  const [hasMoreGmail, setHasMoreGmail] = useState(false);
  const [isLoadingMoreGmail, setIsLoadingMoreGmail] = useState(false);
  const [analyticsExpanded, setAnalyticsExpanded] = useState(false);
  
  // YouTube upload states
  const [selectedVideo, setSelectedVideo] = useState<YouTubeVideoFile | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSelectingVideo, setIsSelectingVideo] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<YouTubeUploadProgress | null>(null);
  const [videoTitle, setVideoTitle] = useState('');
  const [videoDescription, setVideoDescription] = useState('');
  const [videoPrivacy, setVideoPrivacy] = useState<'public' | 'unlisted' | 'private'>('public');
  const [videoCategory, setVideoCategory] = useState('Sports');
  const [connectionStatus, setConnectionStatus] = useState({
    youtube: false,
    twitter: false,
    gmail: false
  });
  const [youtubeChannel, setYoutubeChannel] = useState<any>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const filterDropdownRef = useRef<HTMLDivElement>(null);
  
  // Gmail thread data and modal state
  const [threadData, setThreadData] = useState<{ [threadId: string]: ThreadData }>({});
  const [gmailModalVisible, setGmailModalVisible] = useState(false);
  const [selectedGmailMessage, setSelectedGmailMessage] = useState<GmailMessage | null>(null);
  
  // DM threads state
  const [dmThreads, setDmThreads] = useState<XDMThread[]>([]);
  const [dmThreadsLoading, setDmThreadsLoading] = useState(false);
  const [dmCoachModalOpen, setDmCoachModalOpen] = useState(false);
  // Standalone "Post to X" composer (no coach) — KGH-785 retention play.
  const [postToXOpen, setPostToXOpen] = useState(false);
  const [selectedDMCoach, setSelectedDMCoach] = useState<{ name: string; twitter_handle: string; coach_id?: string; school_id?: string } | null>(null);
  // "Send a DM" picker (APS-253). The card shows only once the server answers
  // with dm_targets.
  const [dmTargets, setDmTargets] = useState<DMTargets | null>(null);
  const [sendDMOpen, setSendDMOpen] = useState(false);
  const [sendDMComposerOpen, setSendDMComposerOpen] = useState(false);
  const [coachLookupMap, setCoachLookupMap] = useState<Map<string, { coach_name: string; school_name: string }>>(new Map());

  const [analyticsData, setAnalyticsData] = useState({
    totalViews: 0,
    totalLikes: 0,
    totalComments: 0,
    totalShares: 0,
    totalPosts: 0,
    totalEmails: 0,
    totalTweets: 0,
    totalVideos: 0,
    averageViewsPerPost: 0,
    averageLikesPerPost: 0,
    engagementRate: '0',
  });

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target as Node)) {
        setFilterDropdownOpen(false);
      }
    };
    
    if (filterDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [filterDropdownOpen]);

  useEffect(() => {
    const loadAthleteProfile = async () => {
      if (!sessionToken) {
        setIsLoading(false);
        return;
      }

      try {
        // Freemium-redesign Phase 1: use the canonical my-athletes endpoint so
        // guardians (whose users.athlete_profile_id is null but who are linked
        // via guardian_athlete_relationships) get their athlete's data instead
        // of an empty page.
        const myAthletes = await familyAPI.getMyAthletes(sessionToken);
        const first = myAthletes.athletes?.[0];
        if (first?.id) {
          setAthleteId(first.id);
          await loadSocialPosts(first.id);
          loadMessagedSchoolCoaches(first.id);
          await loadYouTubeConnectionStatus(first.id);
          loadDMThreads(first.id);
        }
      } catch (error) {
        console.error('Failed to load athlete profile:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadAthleteProfile();
  }, [sessionToken]);

  useEffect(() => {
    if (activeFilter !== 'dms' || !athleteId || !sessionToken) return;
    let cancelled = false;
    fetchDmTargets(sessionToken, athleteId).then((t) => {
      if (!cancelled) setDmTargets(t);
    });
    return () => { cancelled = true; };
  }, [activeFilter, athleteId, sessionToken]);

  // Direct-connect hook for the contextual X DM empty-state button.
  // Reuses the same OAuth popup flow the SocialConnectPanel uses, just
  // wired to a one-off button outside the panel.
  const { connect: connectSocial, connecting } = useSocialConnect({
    athleteId: athleteId || null,
    onConnected: () => {
      if (athleteId) loadSocialPosts(athleteId);
    },
  });

  const loadYouTubeConnectionStatus = async (userId: string) => {
    try {
      const connections = await integrationsAPI.getConnectionStatus(userId);
      setConnectionStatus({
        youtube: connections.youtube || false,
        twitter: connections.twitter || false,
        gmail: connections.gmail || false,
      });

      // If YouTube is connected, load channel info
      if (connections.youtube) {
        const channelData = await integrationsAPI.getYouTubeChannel(userId);
        setYoutubeChannel(channelData?.data || null);

        // Auto-save YouTube channel data to athlete profile
        if (channelData?.data?.id && sessionToken) {
          try {
            await athleteAPI.updateProfile(sessionToken, userId, {
              youtubeChannelUrl: `https://www.youtube.com/channel/${channelData.data.id}`,
              youtubeChannelHandle: channelData.data.title || '',
            });
          } catch (error) {
            console.error('Failed to auto-save YouTube channel data:', error);
            // Silent fail - don't interrupt the user experience
          }
        }
      }
    } catch (error) {
      console.error('Failed to load YouTube connection status:', error);
    }
  };

  const handleSelectVideoFromGallery = () => {
    fileInputRef.current?.click();
  };

  const handleVideoFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      // Create video file object first
      const videoFile: YouTubeVideoFile = {
        file: file,
        name: file.name,
        type: file.type,
        size: file.size,
      };

      // Validate file
      const validation = youtubeUploadService.validateYouTubeVideo(videoFile);
      if (!validation.valid) {
        toast.error(validation.error || 'Invalid video file');
        return;
      }

      setSelectedVideo(videoFile);
      toast.success('Video selected successfully');
    } catch (error) {
      console.error('Error selecting video:', error);
      toast.error('Failed to select video');
    }

    // Reset input
    if (event.target) {
      event.target.value = '';
    }
  };

  const handleClearVideo = () => {
    setSelectedVideo(null);
    setVideoTitle('');
    setVideoDescription('');
    setUploadProgress(null);
  };

  const handleYouTubeUpload = async () => {
    if (!selectedVideo || !videoTitle.trim() || !athleteId) {
      toast.error('Please provide a video and title');
      return;
    }

    if (!sessionToken) {
      toast.error('Session expired. Please log in again.');
      return;
    }

    try {
      setIsUploading(true);

      // Upload to S3
      const result = await youtubeUploadService.uploadYouTubeVideo(
        selectedVideo,
        athleteId,
        sessionToken,
        (progress: YouTubeUploadProgress) => {
          setUploadProgress(progress);
        }
      );

      if (!result.success || !result.video?.url) {
        throw new Error(result.error || 'Failed to upload video to S3');
      }

      // Post to YouTube
      await integrationsAPI.postToYouTube(athleteId, {
        title: videoTitle,
        description: videoDescription || undefined,
        video_urls: [result.video.url],
        options: {
          privacyStatus: videoPrivacy,
        },
      });

      toast.success('Video uploaded to YouTube successfully!');
      
      // Clear form and refresh posts
      handleClearVideo();
      await loadSocialPosts(athleteId);
    } catch (error) {
      console.error('Error uploading to YouTube:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to upload video');
    } finally {
      setIsUploading(false);
      setUploadProgress(null);
    }
  };

  const loadThreadData = async (userId: string, gmailPosts: SocialPost[]) => {
    try {
      const threadMap: { [threadId: string]: ThreadData } = {};

      // First, get all unread replies to know which threads have activity
      const unreadReplies = await integrationsAPI.getUnreadGmailReplies(userId);

      // Map unread counts by thread_id
      const unreadMap: { [threadId: string]: number } = {};
      if (unreadReplies.success && unreadReplies.data?.threads) {
        unreadReplies.data.threads.forEach((thread: any) => {
          if (thread.thread_id && thread.unread_count > 0) {
            unreadMap[thread.thread_id] = thread.unread_count;
          }
        });
      }

      // Load thread details for all Gmail posts with a thread_id in parallel
      // (was a serial N+1 waterfall — one round-trip per thread, awaited in order).
      const postsWithThread = gmailPosts.filter((post: any) => post.emailData?.threadId);
      const threadResults = await Promise.all(
        postsWithThread.map(async (post: any) => {
          const threadId = post.emailData.threadId;
          try {
            const threadDetails = await integrationsAPI.getGmailThread(userId, threadId);
            const processedThreadDetails = threadDetails.data || threadDetails;
            return { threadId, entry: { threadId, unreadCount: unreadMap[threadId] || 0, threadDetails: processedThreadDetails } };
          } catch (threadError) {
            console.error(`Failed to load thread data for ${threadId}:`, threadError);
            // Still keep the basic unread count if we have it
            if (unreadMap[threadId]) {
              return { threadId, entry: { threadId, unreadCount: unreadMap[threadId], threadDetails: null } };
            }
            return null;
          }
        })
      );
      threadResults.forEach((r) => { if (r) threadMap[r.threadId] = r.entry; });

      setThreadData(threadMap);
    } catch (error) {
      console.error('Error loading thread data:', error);
      setThreadData({});
    }
  };

  const loadSocialPosts = async (userId: string, gmailLimitOverride?: number) => {
    const effectiveGmailLimit = gmailLimitOverride ?? gmailLimit;
    if (!userId) return;

    try {
      setIsRefreshing(true);
      // #173: isolate the analytics call so its failure can't abort — and wipe —
      // the whole feed. Gmail/X/YouTube posts still load if analytics is down.
      let analytics: any = { data: null };
      try {
        analytics = await integrationsAPI.getAnalytics(userId);
      } catch (analyticsError) {
        console.error('Error loading social analytics (feed continues):', analyticsError);
      }

      const posts: SocialPost[] = [];

      if (analytics.data?.posts && Array.isArray(analytics.data.posts)) {
        analytics.data.posts.forEach((post: any) => {
          if (post.provider === 'youtube') {
            posts.push({
              id: post.id?.toString() || post.provider_post_id,
              provider_post_id: post.provider_post_id,
              platform: 'youtube',
              type: 'video',
              title: post.title,
              text: post.description || post.content_text,
              thumbnail: post.thumbnail_url,
              media_urls: post.media_urls,
              url: post.video_url,
              date: post.published_at || post.created_at,
              metrics: {
                views: post.view_count || 0,
                likes: post.like_count || 0,
                comments: post.comment_count || 0,
              }
            });
          } else if (post.provider === 'x') {
            posts.push({
              id: post.id?.toString() || post.provider_post_id,
              platform: 'x',
              type: 'tweet',
              text: post.content_text,
              thumbnail: post.thumbnail_url,
              media_urls: post.media_urls,
              url: `https://x.com/i/status/${post.provider_post_id}`,
              date: post.published_at || post.created_at,
              metrics: {
                views: post.view_count || 0,
                likes: post.like_count || 0,
                retweets: post.share_count || 0,
                comments: post.comment_count || 0,
              },
              // Store recipients/coach data for filtering (if available from API)
              recipients: post.recipients || (post.coach_id ? [{
                school_id: post.school_id,
                school_name: post.school_name,
                coach_id: post.coach_id?.toString(),
                coach_name: post.coach_name
              }] : [])
            });
          }
        });
      }

      // Load Gmail emails
      try {
        const gmailData = await integrationsAPI.getSentGmails(userId, { limit: effectiveGmailLimit });
        if (gmailData.success && gmailData.data?.messages && Array.isArray(gmailData.data.messages)) {
          // A full page back means there may be more history to load.
          setHasMoreGmail(gmailData.data.messages.length >= effectiveGmailLimit);
          gmailData.data.messages.forEach((email: any) => {
            // Check if this email has a thread_id for thread monitoring
            if (email.thread_id) {
              // If thread_id !== message_id, this is a reply to an existing thread
              const isReplyToExistingThread = email.thread_id !== email.message_id;

              // Skip this message if it's a reply to an existing thread
              if (isReplyToExistingThread) {
                return; // Skip adding this as a standalone post
              }
            } else {
              return;
            }

            posts.push({
              id: email.message_id || `gmail-${Date.now()}-${Math.random()}`,
              platform: 'gmail',
              type: 'email',
              title: email.subject,
              text: `Sent to ${email.to_emails?.join(', ') || 'Unknown'}${email.is_opened ? ' • Opened' : ' • Not opened yet'}${email.thread_id ? ' • Thread available' : ''}`,
              date: email.timestamp || email.created_at,
              media_urls: email.attachments?.map((att: any) => att.url) || [],
              emailData: {
                to: email.to_emails || [],
                subject: email.subject,
                snippet: `Email tracking: ${email.is_opened ? 'Opened' : 'Not opened yet'}`,
                threadId: email.thread_id,
                messageId: email.message_id,
                isRead: true,
                attachments: email.attachments || [],
              },
              // Store the full Gmail message data for the detail modal
              gmailMessage: email,
              // Build recipients from coach_ids/school_ids arrays for filtering
              recipients: (email.coach_ids && email.school_ids) 
                ? email.coach_ids.map((coachId: string, idx: number) => ({
                    coach_id: coachId,
                    school_id: email.school_ids[idx] ? parseInt(email.school_ids[idx]) : undefined,
                    // Names will be populated by lookup below
                    coach_name: email.coach_names?.[idx],
                    school_name: email.school_names?.[idx]
                  }))
                : []
            });
          });
        } else {
          setHasMoreGmail(false);
        }
      } catch (gmailError) {
        console.error('Error loading sent emails:', gmailError);
      }

      // Load thread data for Gmail items (get complete thread data)
      if (posts.some(post => post.platform === 'gmail')) {
        const gmailPosts = posts.filter(post => post.platform === 'gmail');
        await loadThreadData(userId, gmailPosts);
      }

      // Sort posts by date
      posts.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      // Calculate analytics
      let analyticsSummary = {
        totalViews: 0,
        totalLikes: 0,
        totalComments: 0,
        totalShares: 0,
        totalPosts: 0,
        totalEmails: 0,
        totalTweets: 0,
        totalVideos: 0,
        averageViewsPerPost: 0,
        averageLikesPerPost: 0,
        engagementRate: '0',
      };

      if (analytics.data?.summary) {
        const summary = analytics.data.summary;
        analyticsSummary = {
          totalViews: summary.total_views || 0,
          totalLikes: summary.total_likes || 0,
          totalComments: summary.total_comments || 0,
          totalShares: summary.total_shares || 0,
          totalPosts: summary.total_posts || 0,
          totalEmails: 0,
          totalTweets: summary.posts_by_provider?.x || 0,
          totalVideos: summary.posts_by_provider?.youtube || 0,
          averageViewsPerPost: summary.average_views_per_post || 0,
          averageLikesPerPost: summary.average_likes_per_post || 0,
          engagementRate: summary.engagement_rate || '0',
        };
      }

      const gmailCount = posts.filter(post => post.platform === 'gmail').length;
      analyticsSummary.totalEmails = gmailCount;

      setAnalyticsData(analyticsSummary);
      
      // Collect all unique coach_ids that need name lookup
      const allCoachIds = new Set<string>();
      posts.forEach(post => {
        if (post.recipients) {
          post.recipients.forEach(r => {
            if (r.coach_id && !r.coach_name) {
              allCoachIds.add(r.coach_id);
            }
          });
        }
      });
      
      // Lookup coach/school names if we have IDs without names
      if (allCoachIds.size > 0) {
        try {
          const lookupResponse = await fetch(`${BACKEND_CONFIG.baseURL}/schools/lookup-coaches`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${sessionToken}`
            },
            body: JSON.stringify({ coach_ids: Array.from(allCoachIds) })
          });
          
          if (lookupResponse.ok) {
            const lookupData = await lookupResponse.json();
            if (lookupData.success && lookupData.data) {
              // Build lookup map: coach_id -> { coach_name, school_name, school_id }
              const coachMap = new Map<string, { coach_name: string; school_name: string; school_id: number }>();
              lookupData.data.forEach((coach: any) => {
                coachMap.set(coach.coach_id, {
                  coach_name: coach.coach_name,
                  school_name: coach.school_name,
                  school_id: coach.school_id
                });
              });
              
              // Update posts with names
              posts.forEach(post => {
                if (post.recipients) {
                  post.recipients.forEach(r => {
                    if (r.coach_id && coachMap.has(r.coach_id)) {
                      const info = coachMap.get(r.coach_id)!;
                      r.coach_name = info.coach_name;
                      r.school_name = info.school_name;
                      if (!r.school_id) r.school_id = info.school_id;
                    }
                  });
                }
              });
            }
          }
        } catch (lookupError) {
          console.error('Error looking up coach/school names:', lookupError);
        }
      }
      
      setSocialPosts(posts);

      // Dropdown options come from the dedicated endpoint loaded separately
      // by loadMessagedSchoolCoaches — building from `posts` here was capped
      // at the most recent 20 emails and missed contacted schools.
    } catch (error) {
      // #173: don't wipe an already-rendered feed on a failed (often background)
      // refresh — keep prior posts on screen instead of flashing "No posts yet".
      console.error('Error loading social posts:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Load the next page of sent-email history (grows the requested limit by 20
  // and reloads). Keeps thread fetching proportional to what's shown.
  const handleLoadMoreGmail = async () => {
    if (!athleteId || isLoadingMoreGmail) return;
    const nextLimit = gmailLimit + 20;
    setIsLoadingMoreGmail(true);
    setGmailLimit(nextLimit);
    try {
      await loadSocialPosts(athleteId, nextLimit);
    } finally {
      setIsLoadingMoreGmail(false);
    }
  };

  // Loads the "All Schools & Coaches" dropdown options from the backend.
  // Backed by GET /schools/messaged-school-coaches/:athleteId, which queries
  // integrations.gmail_messages directly so the list reflects every contacted
  // school regardless of how many emails the athlete has sent.
  const loadMessagedSchoolCoaches = async (athleteProfileId: string) => {
    if (!athleteProfileId || !sessionToken) return;
    try {
      const response = await fetch(
        `${BACKEND_CONFIG.baseURL}/schools/messaged-school-coaches/${athleteProfileId}`,
        { headers: { 'Authorization': `Bearer ${sessionToken}` } }
      );
      if (!response.ok) {
        console.error('messaged-school-coaches fetch failed:', response.status);
        return;
      }
      const data = await response.json();
      if (!data?.success || !Array.isArray(data.options)) return;
      const options: SchoolCoachFilter[] = data.options.map((row: any) => ({
        schoolId: row.schoolId,
        coachId: row.coachId,
        label: `${row.schoolName} | ${row.coachName}`,
      }));
      setSchoolCoachOptions(options);
    } catch (error) {
      console.error('loadMessagedSchoolCoaches error:', error);
    }
  };

  const loadDMThreads = async (userId: string) => {
    try {
      setDmThreadsLoading(true);
      const result = await integrationsAPI.getXDMThreads(userId);
      if (result.success && result.data?.threads) {
        setDmThreads(result.data.threads);

        // Lookup coach names for all unique coach_ids
        const allCoachIds = new Set<string>();
        result.data.threads.forEach(thread => {
          thread.coach_ids?.forEach(id => allCoachIds.add(id));
        });

        if (allCoachIds.size > 0 && sessionToken) {
          try {
            const lookupResponse = await fetch(`${BACKEND_CONFIG.baseURL}/schools/lookup-coaches`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
              },
              body: JSON.stringify({ coach_ids: Array.from(allCoachIds) })
            });
            if (lookupResponse.ok) {
              const lookupData = await lookupResponse.json();
              if (lookupData.success && lookupData.data) {
                const map = new Map<string, { coach_name: string; school_name: string }>();
                lookupData.data.forEach((coach: any) => {
                  map.set(coach.coach_id, { coach_name: coach.coach_name, school_name: coach.school_name });
                });
                setCoachLookupMap(map);
              }
            }
          } catch (lookupError) {
            console.error('Error looking up DM coach names:', lookupError);
          }
        }
      }
    } catch (error) {
      console.error('Error loading DM threads:', error);
      setDmThreads([]);
    } finally {
      setDmThreadsLoading(false);
    }
  };

  const handleDMThreadClick = (thread: XDMThread) => {
    const coachId = thread.coach_ids?.[0];
    const schoolId = thread.school_ids?.[0];
    const coachInfo = coachId ? coachLookupMap.get(coachId) : undefined;
    setSelectedDMCoach({
      // audit #245: never surface the internal coach id ("Coach #Unknown") in the UI.
      name: coachInfo?.coach_name || 'Coach',
      twitter_handle: '',
      coach_id: coachId,
      school_id: schoolId,
    });
    setDmCoachModalOpen(true);
  };

  const formatDMDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return d.toLocaleDateString([], { weekday: 'short' });
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const filteredPosts = socialPosts.filter(post => {
    // Platform filter
    let platformMatch = true;
    if (activeFilter === 'gmail') platformMatch = post.platform === 'gmail';
    else if (activeFilter === 'twitter') platformMatch = post.platform === 'x';
    else if (activeFilter === 'youtube') platformMatch = post.platform === 'youtube';
    
    if (!platformMatch) return false;
    
    // School/Coach filter (only applies to gmail and twitter)
    if (schoolCoachFilter && (post.platform === 'gmail' || post.platform === 'x')) {
      const hasMatchingRecipient = post.recipients?.some(
        r => r.school_id === schoolCoachFilter.schoolId && r.coach_id === schoolCoachFilter.coachId
      );
      if (!hasMatchingRecipient) return false;
    }
    
    return true;
  });

  // Filter DM threads by school/coach filter
  const filteredDmThreads = dmThreads.filter(thread => {
    if (!schoolCoachFilter) return true;
    const matchesCoach = thread.coach_ids?.includes(schoolCoachFilter.coachId);
    const matchesSchool = thread.school_ids?.includes(String(schoolCoachFilter.schoolId));
    return matchesCoach || matchesSchool;
  });

  const formatNumber = (num: number): string => {
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  const handlePostClick = (post: SocialPost) => {
    // Handle Gmail posts - open detail modal
    if (post.platform === 'gmail') {
      if (post.gmailMessage) {
        // If we have thread data already loaded, use it
        if (post.emailData?.threadId && threadData[post.emailData.threadId]) {
          // Enhanced Gmail message with thread data
          const enhancedMessage = {
            ...post.gmailMessage,
            threadData: threadData[post.emailData.threadId].threadDetails,
            unreadCount: threadData[post.emailData.threadId].unreadCount
          };

          setSelectedGmailMessage(enhancedMessage);
          setGmailModalVisible(true);
        } else {
          setSelectedGmailMessage(post.gmailMessage);
          setGmailModalVisible(true);
        }
      }
      return;
    }

    // Handle other platforms with URLs
    if (post.url) {
      window.open(post.url, '_blank');
    }
  };

  return (
    <AppLayout>
      <div style={{ padding: isMobile ? '16px' : '24px', maxWidth: '1600px', margin: '0 auto', fontFamily: 'Manrope, sans-serif' }}>
        {/* Collapsible Analytics Header */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          marginBottom: isMobile ? '16px' : '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #E5E7EB'
        }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: isMobile ? '16px' : '20px 24px',
          borderBottom: analyticsExpanded ? '1px solid #E5E7EB' : 'none',
          flexWrap: isMobile ? 'wrap' : 'nowrap',
          gap: isMobile ? '12px' : '0',
        }}>
          <button
            onClick={() => setAnalyticsExpanded(!analyticsExpanded)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontFamily: 'Manrope, sans-serif',
              padding: 0
            }}
          >
            {isRefreshing ? (
              <div style={{
                width: '20px',
                height: '20px',
                border: '3px solid #E5E7EB',
                borderTop: '3px solid #F9550D',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
            ) : (
              <BarChart2 size={20} color="#F9550D" />
            )}
            <span style={{ fontSize: '16px', fontWeight: '600', color: '#151133' }}>
              Analytics Overview
            </span>
            {analyticsExpanded ? (
              <ChevronUp size={18} color="#6B7280" />
            ) : (
              <ChevronDown size={18} color="#6B7280" />
            )}
          </button>

          {/* Configure button removed — the inline SocialConnectPanel
              below the header is the single source for managing
              connections, replacing the old modal. */}
        </div>{analyticsExpanded && (
          <div style={{
            padding: '0 24px 24px 24px',
            borderTop: '1px solid #E5E7EB',
            marginTop: '-1px',
            paddingTop: '24px'
          }}>
            <style>{`
              @media (max-width: 768px) {
                .analytics-container {
                  display: flex !important;
                  flex-direction: column !important;
                  gap: 12px !important;
                }
                .analytics-card {
                  padding: 16px !important;
                  display: flex !important;
                  flex-direction: row !important;
                  align-items: center !important;
                  gap: 16px !important;
                  text-align: left !important;
                }
                .analytics-card-icon-wrapper {
                  flex-shrink: 0;
                  width: 48px;
                  height: 48px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  background: white;
                  border-radius: 12px;
                }
                .analytics-card-content {
                  flex: 1;
                  display: flex;
                  flex-direction: column;
                  gap: 4px;
                }
                .analytics-card-value {
                  font-size: 24px !important;
                }
                .analytics-card-label {
                  font-size: 14px !important;
                }
              }
              @media (min-width: 769px) {
                .analytics-row-1 {
                  display: grid;
                  grid-template-columns: repeat(4, 1fr);
                  gap: 16px;
                  margin-bottom: 16px;
                }
                .analytics-row-2 {
                  display: grid;
                  grid-template-columns: repeat(4, 1fr);
                  gap: 16px;
                  margin-bottom: 16px;
                }
                .analytics-row-3 {
                  display: grid;
                  grid-template-columns: repeat(3, 1fr);
                  gap: 16px;
                }
              }
            `}</style>
            
            <div className="analytics-container">
              {/* Row 1 - 4 cards */}
              <div className="analytics-row-1">
                <div className="analytics-card" style={{ textAlign: 'center', padding: '24px 16px', background: '#FAFBFF', borderRadius: '12px' }}>
                  <div className="analytics-card-icon-wrapper">
                    <Mail size={32} color="#F9550D" />
                  </div>
                  <div className="analytics-card-content">
                    <div className="analytics-card-value" style={{ fontSize: '32px', fontWeight: '700', color: '#151133', marginBottom: '6px' }}>
                      {analyticsData.totalEmails}
                    </div>
                    <div className="analytics-card-label" style={{ fontSize: '14px', color: '#6B7280', fontWeight: '500' }}>Emails</div>
                  </div>
                </div>

                <div className="analytics-card" style={{ textAlign: 'center', padding: '24px 16px', background: '#FAFBFF', borderRadius: '12px' }}>
                  <div className="analytics-card-icon-wrapper">
                    <PlayCircle size={32} color="#FF0000" />
                  </div>
                  <div className="analytics-card-content">
                    <div className="analytics-card-value" style={{ fontSize: '32px', fontWeight: '700', color: '#151133', marginBottom: '6px' }}>
                      {analyticsData.totalVideos}
                    </div>
                    <div className="analytics-card-label" style={{ fontSize: '14px', color: '#6B7280', fontWeight: '500' }}>Videos</div>
                  </div>
                </div>

                <div className="analytics-card" style={{ textAlign: 'center', padding: '24px 16px', background: '#FAFBFF', borderRadius: '12px' }}>
                  <div className="analytics-card-icon-wrapper">
                    <XIcon size={32} color="#000000" />
                  </div>
                  <div className="analytics-card-content">
                    <div className="analytics-card-value" style={{ fontSize: '32px', fontWeight: '700', color: '#151133', marginBottom: '6px' }}>
                      {analyticsData.totalTweets}
                    </div>
                    <div className="analytics-card-label" style={{ fontSize: '14px', color: '#6B7280', fontWeight: '500' }}>Tweets</div>
                  </div>
                </div>

                <div className="analytics-card" style={{ textAlign: 'center', padding: '24px 16px', background: '#FAFBFF', borderRadius: '12px' }}>
                  <div className="analytics-card-icon-wrapper">
                    <Eye size={32} color="#F9550D" />
                  </div>
                  <div className="analytics-card-content">
                    <div className="analytics-card-value" style={{ fontSize: '32px', fontWeight: '700', color: '#151133', marginBottom: '6px' }}>
                      {formatNumber(analyticsData.totalViews)}
                    </div>
                    <div className="analytics-card-label" style={{ fontSize: '14px', color: '#6B7280', fontWeight: '500' }}>Views</div>
                  </div>
                </div>
              </div>

              {/* Row 2 - 4 cards */}
              <div className="analytics-row-2">
                <div className="analytics-card" style={{ textAlign: 'center', padding: '24px 16px', background: '#FAFBFF', borderRadius: '12px' }}>
                  <div className="analytics-card-icon-wrapper">
                    <Heart size={32} color="#EC4899" />
                  </div>
                  <div className="analytics-card-content">
                    <div className="analytics-card-value" style={{ fontSize: '32px', fontWeight: '700', color: '#151133', marginBottom: '6px' }}>
                      {formatNumber(analyticsData.totalLikes)}
                    </div>
                    <div className="analytics-card-label" style={{ fontSize: '14px', color: '#6B7280', fontWeight: '500' }}>Likes</div>
                  </div>
                </div>

                <div className="analytics-card" style={{ textAlign: 'center', padding: '24px 16px', background: '#FAFBFF', borderRadius: '12px' }}>
                  <div className="analytics-card-icon-wrapper">
                    <MessageCircle size={32} color="#F9550D" />
                  </div>
                  <div className="analytics-card-content">
                    <div className="analytics-card-value" style={{ fontSize: '32px', fontWeight: '700', color: '#151133', marginBottom: '6px' }}>
                      {formatNumber(analyticsData.totalComments)}
                    </div>
                    <div className="analytics-card-label" style={{ fontSize: '14px', color: '#6B7280', fontWeight: '500' }}>Comments</div>
                  </div>
                </div>

                <div className="analytics-card" style={{ textAlign: 'center', padding: '24px 16px', background: '#FAFBFF', borderRadius: '12px' }}>
                  <div className="analytics-card-icon-wrapper">
                    <Share size={32} color="#10B981" />
                  </div>
                  <div className="analytics-card-content">
                    <div className="analytics-card-value" style={{ fontSize: '32px', fontWeight: '700', color: '#151133', marginBottom: '6px' }}>
                      {formatNumber(analyticsData.totalShares)}
                    </div>
                    <div className="analytics-card-label" style={{ fontSize: '14px', color: '#6B7280', fontWeight: '500' }}>Shares</div>
                  </div>
                </div>

                <div className="analytics-card" style={{ textAlign: 'center', padding: '24px 16px', background: '#FAFBFF', borderRadius: '12px' }}>
                  <div className="analytics-card-icon-wrapper">
                    <TrendingUp size={32} color="#8B5CF6" />
                  </div>
                  <div className="analytics-card-content">
                    <div className="analytics-card-value" style={{ fontSize: '32px', fontWeight: '700', color: '#151133', marginBottom: '6px' }}>
                      {formatNumber(analyticsData.averageViewsPerPost)}
                    </div>
                    <div className="analytics-card-label" style={{ fontSize: '14px', color: '#6B7280', fontWeight: '500' }}>Avg Views</div>
                  </div>
                </div>
              </div>

              {/* Row 3 - 3 cards */}
              <div className="analytics-row-3">
                <div className="analytics-card" style={{ textAlign: 'center', padding: '24px 16px', background: '#FAFBFF', borderRadius: '12px' }}>
                  <div className="analytics-card-icon-wrapper">
                    <ThumbsUp size={32} color="#F59E0B" />
                  </div>
                  <div className="analytics-card-content">
                    <div className="analytics-card-value" style={{ fontSize: '32px', fontWeight: '700', color: '#151133', marginBottom: '6px' }}>
                      {formatNumber(analyticsData.averageLikesPerPost)}
                    </div>
                    <div className="analytics-card-label" style={{ fontSize: '14px', color: '#6B7280', fontWeight: '500' }}>Avg Likes</div>
                  </div>
                </div>

                <div className="analytics-card" style={{ textAlign: 'center', padding: '24px 16px', background: '#FAFBFF', borderRadius: '12px' }}>
                  <div className="analytics-card-icon-wrapper">
                    <Activity size={32} color="#EF4444" />
                  </div>
                  <div className="analytics-card-content">
                    <div className="analytics-card-value" style={{ fontSize: '32px', fontWeight: '700', color: '#151133', marginBottom: '6px' }}>
                      {analyticsData.engagementRate}%
                    </div>
                    <div className="analytics-card-label" style={{ fontSize: '14px', color: '#6B7280', fontWeight: '500' }}>Engagement</div>
                  </div>
                </div>

                <div className="analytics-card" style={{ 
                  textAlign: 'center', 
                  padding: '24px 16px', 
                  background: '#FAFBFF', 
                  borderRadius: '12px'
                }}>
                  <div className="analytics-card-icon-wrapper">
                    <Grid size={32} color="#F9550D" />
                  </div>
                  <div className="analytics-card-content">
                    <div className="analytics-card-value" style={{ fontSize: '32px', fontWeight: '700', color: '#151133', marginBottom: '6px' }}>
                      {analyticsData.totalPosts}
                    </div>
                    <div className="analytics-card-label" style={{ fontSize: '14px', color: '#6B7280', fontWeight: '500' }}>Total Posts</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Social connections — inline replacement for the old Configure
          modal. Collapsed by default whenever Messages opens so the feed
          leads; the user can expand the header to manage connections. */}
      <SocialConnectPanel
        athleteId={athleteId || null}
        collapsible
        defaultCollapsed
        onRefresh={() => {
          if (athleteId) {
            loadSocialPosts(athleteId);
          }
        }}
      />

      {/* Filter Tabs */}
      <div 
        className="hide-scrollbar"
        style={{
          background: 'white',
          borderRadius: '12px',
          padding: '8px',
          marginBottom: '24px',
          display: 'flex',
          gap: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          border: '1px solid #E5E7EB',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch'
        }}>
        <button
          onClick={() => setActiveFilter('all')}
          style={{
            flex: isMobile ? 'none' : 1,
            minWidth: isMobile ? 'auto' : undefined,
            padding: '12px 16px',
            border: 'none',
            borderRadius: '8px',
            background: activeFilter === 'all' ? '#F9550D' : 'transparent',
            color: activeFilter === 'all' ? 'white' : '#6B7280',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'all 0.2s',
            fontFamily: 'Manrope, sans-serif',
            whiteSpace: 'nowrap',
            flexShrink: 0
          }}
          onMouseEnter={(e) => {
            if (activeFilter !== 'all') e.currentTarget.style.background = '#F9FAFB';
          }}
          onMouseLeave={(e) => {
            if (activeFilter !== 'all') e.currentTarget.style.background = 'transparent';
          }}
        >
          <Grid size={16} />
          <span>All</span>
        </button>

        <button
          onClick={() => setActiveFilter('gmail')}
          style={{
            flex: isMobile ? 'none' : 1,
            minWidth: isMobile ? 'auto' : undefined,
            padding: '12px 16px',
            border: 'none',
            borderRadius: '8px',
            background: activeFilter === 'gmail' ? '#EA4335' : 'transparent',
            color: activeFilter === 'gmail' ? 'white' : '#6B7280',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'all 0.2s',
            fontFamily: 'Manrope, sans-serif',
            whiteSpace: 'nowrap',
            flexShrink: 0
          }}
          onMouseEnter={(e) => {
            if (activeFilter !== 'gmail') e.currentTarget.style.background = '#F9FAFB';
          }}
          onMouseLeave={(e) => {
            if (activeFilter !== 'gmail') e.currentTarget.style.background = 'transparent';
          }}
        >
          <Mail size={16} />
          <span>Gmail</span>
        </button>

        <button
          onClick={() => setActiveFilter('twitter')}
          style={{
            flex: isMobile ? 'none' : 1,
            minWidth: isMobile ? 'auto' : undefined,
            padding: '12px 16px',
            border: 'none',
            borderRadius: '8px',
            background: activeFilter === 'twitter' ? '#000000' : 'transparent',
            color: activeFilter === 'twitter' ? 'white' : '#6B7280',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'all 0.2s',
            fontFamily: 'Manrope, sans-serif',
            whiteSpace: 'nowrap',
            flexShrink: 0
          }}
          onMouseEnter={(e) => {
            if (activeFilter !== 'twitter') e.currentTarget.style.background = '#F9FAFB';
          }}
          onMouseLeave={(e) => {
            if (activeFilter !== 'twitter') e.currentTarget.style.background = 'transparent';
          }}
        >
          <XIcon size={16} />
          <span>X</span>
        </button>

        <button
          onClick={() => setActiveFilter('dms')}
          style={{
            flex: isMobile ? 'none' : 1,
            minWidth: isMobile ? 'auto' : undefined,
            padding: '12px 16px',
            border: 'none',
            borderRadius: '8px',
            background: activeFilter === 'dms' ? '#151133' : 'transparent',
            color: activeFilter === 'dms' ? 'white' : '#6B7280',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'all 0.2s',
            fontFamily: 'Manrope, sans-serif',
            whiteSpace: 'nowrap',
            flexShrink: 0
          }}
          onMouseEnter={(e) => {
            if (activeFilter !== 'dms') e.currentTarget.style.background = '#F9FAFB';
          }}
          onMouseLeave={(e) => {
            if (activeFilter !== 'dms') e.currentTarget.style.background = 'transparent';
          }}
        >
          <XIcon size={16} />
          <span>DMs</span>
        </button>

        <button
          onClick={() => setActiveFilter('youtube')}
          style={{
            flex: isMobile ? 'none' : 1,
            minWidth: isMobile ? 'auto' : undefined,
            padding: '12px 16px',
            border: 'none',
            borderRadius: '8px',
            background: activeFilter === 'youtube' ? '#FF0000' : 'transparent',
            color: activeFilter === 'youtube' ? 'white' : '#6B7280',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'all 0.2s',
            fontFamily: 'Manrope, sans-serif',
            whiteSpace: 'nowrap',
            flexShrink: 0
          }}
          onMouseEnter={(e) => {
            if (activeFilter !== 'youtube') e.currentTarget.style.background = '#F9FAFB';
          }}
          onMouseLeave={(e) => {
            if (activeFilter !== 'youtube') e.currentTarget.style.background = 'transparent';
          }}
        >
          <PlayCircle size={16} />
          <span>YouTube</span>
        </button>
      </div>

      {/* School/Coach Filter Dropdown - Only show for Gmail filter (emails sent to coaches) */}
      {(activeFilter === 'gmail' || activeFilter === 'all') && schoolCoachOptions.length > 0 && (
        <div 
          ref={filterDropdownRef}
          style={{
            position: 'relative',
            marginBottom: '24px'
          }}
        >
          <button
            onClick={() => setFilterDropdownOpen(!filterDropdownOpen)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 16px',
              background: schoolCoachFilter ? '#F9550D' : 'white',
              color: schoolCoachFilter ? 'white' : '#374151',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              fontFamily: 'Manrope, sans-serif',
              minWidth: '200px',
              justifyContent: 'space-between'
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Filter size={16} />
              {schoolCoachFilter ? schoolCoachFilter.label : 'All Schools & Coaches'}
            </span>
            <ChevronDown size={16} style={{ 
              transform: filterDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s'
            }} />
          </button>
          
          {filterDropdownOpen && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: '4px',
              background: 'white',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              zIndex: 100,
              minWidth: '280px',
              maxHeight: '300px',
              overflowY: 'auto'
            }}>
              {/* All option */}
              <button
                onClick={() => {
                  setSchoolCoachFilter(null);
                  setFilterDropdownOpen(false);
                }}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  background: !schoolCoachFilter ? '#FFF7ED' : 'transparent',
                  color: '#374151',
                  fontSize: '14px',
                  fontWeight: !schoolCoachFilter ? '600' : '400',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontFamily: 'Manrope, sans-serif',
                  borderBottom: '1px solid #E5E7EB'
                }}
                onMouseEnter={(e) => {
                  if (schoolCoachFilter) e.currentTarget.style.background = '#F9FAFB';
                }}
                onMouseLeave={(e) => {
                  if (schoolCoachFilter) e.currentTarget.style.background = 'transparent';
                }}
              >
                All Schools & Coaches
              </button>
              
              {/* School/Coach options */}
              {schoolCoachOptions.map((option, index) => (
                <button
                  key={`${option.schoolId}-${option.coachId}`}
                  onClick={() => {
                    setSchoolCoachFilter(option);
                    setFilterDropdownOpen(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: 'none',
                    background: schoolCoachFilter?.schoolId === option.schoolId && 
                               schoolCoachFilter?.coachId === option.coachId ? '#FFF7ED' : 'transparent',
                    color: '#374151',
                    fontSize: '14px',
                    fontWeight: schoolCoachFilter?.schoolId === option.schoolId && 
                               schoolCoachFilter?.coachId === option.coachId ? '600' : '400',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontFamily: 'Manrope, sans-serif',
                    borderBottom: index < schoolCoachOptions.length - 1 ? '1px solid #F3F4F6' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (!(schoolCoachFilter?.schoolId === option.schoolId && 
                          schoolCoachFilter?.coachId === option.coachId)) {
                      e.currentTarget.style.background = '#F9FAFB';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!(schoolCoachFilter?.schoolId === option.schoolId && 
                          schoolCoachFilter?.coachId === option.coachId)) {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* YouTube Upload Section - Only show when YouTube filter is active */}
      {/* Post to X — standalone composer on the X tab (KGH-785). Lets athletes
          post to X from APS with no coach tagged, and drop in a saved tag list. */}
      {activeFilter === 'twitter' && connectionStatus.twitter && (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '20px 24px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #E5E7EB',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '16px',
          flexWrap: 'wrap',
        }}>
          <div>
            <h3 style={{ margin: '0 0 4px 0', fontSize: '18px', fontWeight: 700, color: '#111827', fontFamily: 'Manrope, sans-serif' }}>
              Post to X
            </h3>
            <p style={{ margin: 0, fontSize: '14px', color: '#6B7280' }}>
              Share an update to your X — tag your team or a saved list, no coach required.
            </p>
          </div>
          <button
            onClick={() => setPostToXOpen(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              padding: '12px 20px', background: '#000000', color: 'white',
              border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: 600,
              cursor: 'pointer', fontFamily: 'Manrope, sans-serif', whiteSpace: 'nowrap',
            }}
          >
            <XIcon size={16} />
            New Post
          </button>
        </div>
      )}

      {activeFilter === 'youtube' && connectionStatus.youtube && (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #E5E7EB'
        }}>
          {/* YouTube Upload Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '24px',
            marginBottom: youtubeChannel ? '24px' : '0',
            paddingBottom: youtubeChannel ? '20px' : '0',
            borderBottom: youtubeChannel ? '1px solid #E5E7EB' : 'none'
          }}>
            {/* Channel Info - Left Side */}
            <div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '4px'
              }}>
                <PlayCircle size={20} color="#FF0000" />
                <h3 style={{
                  margin: 0,
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#111827',
                  fontFamily: 'Manrope, sans-serif'
                }}>
                  {youtubeChannel?.title || 'Upload Video'}
                </h3>
              </div>
              {youtubeChannel && (
                <>
                  <p style={{
                    margin: '2px 0',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#6B7280'
                  }}>
                    {youtubeChannel.subscriber_count
                      ? `${youtubeChannel.subscriber_count.toLocaleString()} subscribers`
                      : 'YouTube Channel'}
                  </p>
                  {youtubeChannel.video_count !== undefined && (
                    <p style={{
                      margin: '2px 0 0',
                      fontSize: '13px',
                      color: '#9CA3AF'
                    }}>
                      {youtubeChannel.video_count.toLocaleString()} videos • {(youtubeChannel.view_count || 0).toLocaleString()} total views
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Upload Button - Right Side */}
            {!selectedVideo && !isUploading && (
              <button
                onClick={handleSelectVideoFromGallery}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 20px',
                  background: '#FF0000',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontFamily: 'Manrope, sans-serif',
                  transition: 'all 0.2s',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#CC0000';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 0, 0, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#FF0000';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <Folder size={18} />
                Choose Video
              </button>
            )}
          </div>

          {/* Selected Video Preview */}
          {selectedVideo && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px',
              background: '#F9FAFB',
              borderRadius: '8px',
              marginBottom: '16px',
              border: '1px solid #E5E7EB'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Video size={32} color="#6B7280" />
                <div>
                  <p style={{
                    margin: 0,
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#111827',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '300px'
                  }}>
                    {selectedVideo.name}
                  </p>
                  {selectedVideo.size && (
                    <p style={{
                      margin: '4px 0 0',
                      fontSize: '12px',
                      color: '#6B7280'
                    }}>
                      {(selectedVideo.size / (1024 * 1024)).toFixed(1)} MB
                    </p>
                  )}
                </div>
              </div>
              {!isUploading && (
                <button
                  onClick={handleClearVideo}
                  style={{
                    padding: '6px',
                    background: 'transparent',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#FEE2E2'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <X size={20} color="#EF4444" />
                </button>
              )}
            </div>
          )}

          {/* Video Selection Button - Hidden since button is now in header */}

          {/* Upload Progress */}
          {isUploading && uploadProgress && (
            <div style={{
              padding: '16px',
              background: '#F9FAFB',
              borderRadius: '8px',
              marginBottom: '16px',
              border: '1px solid #E5E7EB'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <span style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>
                  {uploadProgress.message}
                </span>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#F9550D' }}>
                  {uploadProgress.percentage}%
                </span>
              </div>
              <div style={{
                width: '100%',
                height: '8px',
                background: '#E5E7EB',
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${uploadProgress.percentage}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #F9550D 0%, #FF7A3D 100%)',
                  transition: 'width 0.3s ease'
                }} />
              </div>
              <p style={{
                margin: '8px 0 0',
                fontSize: '12px',
                color: '#6B7280'
              }}>
                Stage: {uploadProgress.stage}
              </p>
            </div>
          )}

          {/* Video Upload Form */}
          {selectedVideo && !isUploading && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Title Input */}
              <div>
                {/* All four controls in this composer were hand-styled, each
                    repainting its own focus border on onFocus/onBlur and each
                    setting `outline: none` — so none of them showed a keyboard
                    focus ring. The primitives draw both from .aps-input.
                    The labels now point at the controls, which they did not. */}
                <label htmlFor="yt-video-title" style={{
                  display: 'block',
                  marginBottom: '6px',
                  fontSize: '13px',
                  fontWeight: '500',
                  color: '#111827',
                  fontFamily: 'Manrope, sans-serif'
                }}>
                  Title *
                </label>
                <TextInput
                  id="yt-video-title"
                  type="text"
                  value={videoTitle}
                  onChange={(e) => setVideoTitle(e.target.value)}
                  placeholder="Enter video title..."
                  maxLength={100}
                />
                <p style={{
                  margin: '4px 0 0',
                  fontSize: '12px',
                  color: '#6B7280',
                  textAlign: 'right'
                }}>
                  {videoTitle.length}/100
                </p>
              </div>

              {/* Description Input */}
              <div>
                <label htmlFor="yt-video-description" style={{
                  display: 'block',
                  marginBottom: '6px',
                  fontSize: '13px',
                  fontWeight: '500',
                  color: '#111827',
                  fontFamily: 'Manrope, sans-serif'
                }}>
                  Description
                </label>
                <TextArea
                  id="yt-video-description"
                  value={videoDescription}
                  onChange={(e) => setVideoDescription(e.target.value)}
                  placeholder="Enter video description..."
                  maxLength={500}
                  rows={4}
                />
                <p style={{
                  margin: '4px 0 0',
                  fontSize: '12px',
                  color: '#6B7280',
                  textAlign: 'right'
                }}>
                  {videoDescription.length}/500
                </p>
              </div>

              {/* Privacy and Category Row */}
              <div style={{ display: 'flex', gap: '12px' }}>
                {/* Privacy Select */}
                <div style={{ flex: 1 }}>
                  <label htmlFor="yt-video-privacy" style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontSize: '13px',
                    fontWeight: '500',
                    color: '#111827',
                    fontFamily: 'Manrope, sans-serif'
                  }}>
                    Privacy
                  </label>
                  {/* The inline data-URI chevron and the right padding reserved
                      for it go with the native control — Select renders a real
                      react-feather chevron and reserves its own room. */}
                  <Select
                    id="yt-video-privacy"
                    value={videoPrivacy}
                    onChange={(e) => setVideoPrivacy(e.target.value as 'public' | 'unlisted' | 'private')}
                  >
                    <option value="public">Public</option>
                    <option value="unlisted">Unlisted</option>
                    <option value="private">Private</option>
                  </Select>
                </div>

                {/* Category Select */}
                <div style={{ flex: 1 }}>
                  <label htmlFor="yt-video-category" style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontSize: '13px',
                    fontWeight: '500',
                    color: '#111827',
                    fontFamily: 'Manrope, sans-serif'
                  }}>
                    Category
                  </label>
                  <Select
                    id="yt-video-category"
                    value={videoCategory}
                    onChange={(e) => setVideoCategory(e.target.value)}
                  >
                    <option value="Sports">Sports</option>
                    <option value="Education">Education</option>
                    <option value="Entertainment">Entertainment</option>
                    <option value="People & Blogs">People & Blogs</option>
                    <option value="Howto & Style">Howto & Style</option>
                    <option value="Travel & Events">Travel & Events</option>
                  </Select>
                </div>
              </div>

              {/* Upload Button */}
              <button
                onClick={handleYouTubeUpload}
                disabled={!videoTitle.trim() || isUploading}
                style={{
                  width: '100%',
                  padding: '12px 20px',
                  background: !videoTitle.trim() || isUploading ? '#9CA3AF' : '#FF0000',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: !videoTitle.trim() || isUploading ? 'not-allowed' : 'pointer',
                  fontFamily: 'Manrope, sans-serif',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (videoTitle.trim() && !isUploading) e.currentTarget.style.background = '#CC0000';
                }}
                onMouseLeave={(e) => {
                  if (videoTitle.trim() && !isUploading) e.currentTarget.style.background = '#FF0000';
                }}
              >
                {isUploading ? 'Uploading...' : 'Upload to YouTube'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="video/mp4,video/mov,video/quicktime,video/avi,video/x-msvideo,video/webm,video/mkv"
        onChange={handleVideoFileSelected}
        style={{ display: 'none' }}
      />

      {/* Active filter chip (audit #211): make the school/coach filter visible and
          clearable so an empty result set reads as "filtered", not "not connected". */}
      {schoolCoachFilter && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '13px', color: '#6B7280', fontFamily: 'Manrope, sans-serif' }}>Filtered by:</span>
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 10px',
            background: '#FFF7ED',
            border: '1px solid #F9550D',
            borderRadius: '16px',
            fontSize: '13px',
            fontWeight: 600,
            color: '#F9550D',
            fontFamily: 'Manrope, sans-serif'
          }}>
            {schoolCoachFilter.label}
            <button
              onClick={() => setSchoolCoachFilter(null)}
              aria-label="Clear school and coach filter"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                color: '#F9550D'
              }}
            >
              <X size={14} aria-hidden="true" />
            </button>
          </span>
        </div>
      )}

      {/* Content */}
      {isLoading ? (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '80px 24px',
          textAlign: 'center',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #E5E7EB'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #E5E7EB',
            borderTop: '4px solid #F9550D',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>Loading social posts...</p>
        </div>
      ) : activeFilter === 'dms' ? (
        /* DM Threads View */
        <div>
          {/* Send a DM (APS-253): pick a school, then one coach. */}
          {connectionStatus.twitter && dmTargets && !dmThreadsLoading && (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '20px 24px',
              marginBottom: '16px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #E5E7EB',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '16px',
              flexWrap: 'wrap',
            }}>
              <div>
                <h3 style={{ margin: '0 0 4px 0', fontSize: '18px', fontWeight: 700, color: '#111827', fontFamily: 'Manrope, sans-serif' }}>
                  Send a DM
                </h3>
                <p style={{ margin: 0, fontSize: '14px', color: '#6B7280' }}>
                  Message one coach at a school on your lists.
                </p>
              </div>
              <button
                onClick={() => setSendDMOpen(true)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '8px',
                  padding: '12px 20px', background: '#000000', color: 'white',
                  border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'Manrope, sans-serif', whiteSpace: 'nowrap',
                }}
              >
                <MessageCircle size={16} aria-hidden />
                New DM
              </button>
            </div>
          )}
          {dmThreadsLoading ? (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '80px 24px',
              textAlign: 'center',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #E5E7EB'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                border: '4px solid #E5E7EB',
                borderTop: '4px solid #000000',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 16px'
              }} />
              <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>Loading DM conversations...</p>
            </div>
          ) : !connectionStatus.twitter ? (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '80px 24px',
              textAlign: 'center',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #E5E7EB'
            }}>
              <XIcon size={48} color="#000000" style={{ marginBottom: '16px' }} />
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', margin: '0 0 8px 0' }}>
                X Account Not Connected
              </h3>
              <p style={{ fontSize: '14px', color: '#6B7280', margin: '0 0 16px 0' }}>
                Connect your X account to view and send direct messages to coaches.
              </p>
              <button
                onClick={() => connectSocial('twitter')}
                disabled={connecting === 'twitter'}
                style={{
                  padding: '10px 20px',
                  background: connecting === 'twitter' ? '#9CA3AF' : '#000000',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: connecting === 'twitter' ? 'default' : 'pointer',
                  fontFamily: 'Manrope, sans-serif'
                }}
              >
                {connecting === 'twitter' ? 'Connecting…' : 'Connect X Account'}
              </button>
            </div>
          ) : dmThreads.length === 0 ? (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '80px 24px',
              textAlign: 'center',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #E5E7EB'
            }}>
              <XIcon size={48} color="#D1D5DB" style={{ marginBottom: '16px' }} />
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', margin: '0 0 8px 0' }}>
                No DM conversations yet
              </h3>
              <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>
                Send a DM to a coach from their profile to start a conversation
              </p>
            </div>
          ) : filteredDmThreads.length === 0 ? (
            /* audit #211: threads exist but the school/coach filter hides them all —
               say so (and offer to clear) instead of rendering a blank list. */
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '80px 24px',
              textAlign: 'center',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #E5E7EB'
            }}>
              <Filter size={48} color="#D1D5DB" style={{ marginBottom: '16px' }} />
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', margin: '0 0 8px 0' }}>
                No conversations match this filter
              </h3>
              <p style={{ fontSize: '14px', color: '#6B7280', margin: '0 0 16px 0' }}>
                {schoolCoachFilter ? `No DMs with ${schoolCoachFilter.label}.` : 'No DMs match the current filter.'}
              </p>
              {schoolCoachFilter && (
                <button
                  onClick={() => setSchoolCoachFilter(null)}
                  style={{
                    padding: '10px 20px',
                    background: '#F9550D',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontFamily: 'Manrope, sans-serif'
                  }}
                >
                  Clear filter
                </button>
              )}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {filteredDmThreads.map((thread) => {
                const coachId = thread.coach_ids?.[0];
                const coachInfo = coachId ? coachLookupMap.get(coachId) : undefined;
                const lastMessage = thread.messages?.[thread.messages.length - 1];
                const coachName = coachInfo?.coach_name || 'Coach'; // audit #245: no internal id in UI
                const schoolName = coachInfo?.school_name;

                return (
                  <div
                    key={thread.dm_conversation_id}
                    role="button"
                    tabIndex={0}
                    aria-label={`Open direct message conversation with ${coachName}`}
                    onClick={() => handleDMThreadClick(thread)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleDMThreadClick(thread);
                      }
                    }}
                    style={{
                      background: 'white',
                      borderRadius: '12px',
                      padding: '16px 20px',
                      cursor: 'pointer',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                      border: '1px solid #E5E7EB',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                      e.currentTarget.style.borderColor = '#000000';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
                      e.currentTarget.style.borderColor = '#E5E7EB';
                    }}
                  >
                    {/* Avatar */}
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      backgroundColor: '#F3F4F6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <XIcon size={22} color="#000000" />
                    </div>

                    {/* Content */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <h3 style={{
                          margin: 0,
                          fontSize: '15px',
                          fontWeight: '600',
                          color: '#151133',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {coachName}
                        </h3>
                        <span style={{
                          fontSize: '12px',
                          color: '#9CA3AF',
                          whiteSpace: 'nowrap',
                          marginLeft: '12px'
                        }}>
                          {formatDMDate(thread.updated_at)}
                        </span>
                      </div>
                      {schoolName && (
                        <p style={{
                          margin: '0 0 4px',
                          fontSize: '12px',
                          color: '#F9550D',
                          fontWeight: '500'
                        }}>
                          {schoolName}
                        </p>
                      )}
                      {lastMessage && (
                        <p style={{
                          margin: 0,
                          fontSize: '13px',
                          color: '#6B7280',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {lastMessage.is_sent_by_user ? 'You: ' : ''}{lastMessage.text}
                        </p>
                      )}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginTop: '6px'
                      }}>
                        <span style={{ fontSize: '12px', color: '#9CA3AF' }}>
                          {thread.messages?.length || 0} message{(thread.messages?.length || 0) !== 1 ? 's' : ''}
                        </span>
                        <a
                          href="https://x.com/messages"
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            fontSize: '12px',
                            color: '#000000',
                            textDecoration: 'none',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          Continue on X <ArrowRight size={12} aria-hidden="true" />
                        </a>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (filteredPosts.length > 0 || (activeFilter === 'all' && filteredDmThreads.length > 0)) ? (
        <div>
          {/* DM Thread Cards in All view */}
          {activeFilter === 'all' && filteredDmThreads.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#6B7280', marginBottom: '12px', fontFamily: 'Manrope, sans-serif', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Direct Messages
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {filteredDmThreads.map((thread) => {
                  const coachId = thread.coach_ids?.[0];
                  const coachInfo = coachId ? coachLookupMap.get(coachId) : undefined;
                  const lastMessage = thread.messages?.[thread.messages.length - 1];
                  const coachName = coachInfo?.coach_name || 'Coach'; // audit #245: no internal id in UI
                  const schoolName = coachInfo?.school_name;

                  return (
                    <div
                      key={`dm-${thread.dm_conversation_id}`}
                      role="button"
                      tabIndex={0}
                      aria-label={`Open direct message conversation with ${coachName}`}
                      onClick={() => handleDMThreadClick(thread)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleDMThreadClick(thread);
                        }
                      }}
                      style={{
                        background: 'white',
                        borderRadius: '12px',
                        padding: '12px 16px',
                        cursor: 'pointer',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                        border: '1px solid #E5E7EB',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#000000';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#E5E7EB';
                      }}
                    >
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor: '#F3F4F6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <XIcon size={18} color="#000000" />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '14px', fontWeight: '600', color: '#151133' }}>{coachName}</span>
                          {lastMessage && <span style={{ fontSize: '11px', color: '#9CA3AF' }}>{formatDMDate(thread.updated_at)}</span>}
                        </div>
                        {schoolName && <p style={{ margin: 0, fontSize: '12px', color: '#F9550D', fontWeight: '500' }}>{schoolName}</p>}
                        {lastMessage && (
                          <p style={{ margin: 0, fontSize: '12px', color: '#6B7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {lastMessage.is_sent_by_user ? 'You: ' : ''}{lastMessage.text}
                          </p>
                        )}
                      </div>
                      <a
                        href="https://x.com/messages"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{ fontSize: '11px', color: '#000000', textDecoration: 'none', fontWeight: '500', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: '3px' }}
                      >
                        Continue on X <ArrowRight size={11} aria-hidden="true" />
                      </a>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        <Masonry
          breakpointCols={{
            default: 3,
            1200: 2,
            768: 1
          }}
          className="masonry-grid"
          columnClassName="masonry-grid-column"
        >
          {filteredPosts.map((post, index) => (
            <div
              key={`${post.platform}-${post.id}-${index}`}
              onClick={() => handlePostClick(post)}
              style={{
                background: 'white',
                borderRadius: '12px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                border: '1px solid #E5E7EB',
                cursor: (post.url || post.platform === 'gmail') ? 'pointer' : 'default',
                transition: 'all 0.2s',
                overflow: 'hidden',
                marginBottom: '20px'
              }}
              onMouseEnter={(e) => {
                if (post.url || post.platform === 'gmail') {
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }
              }}
              onMouseLeave={(e) => {
                if (post.url || post.platform === 'gmail') {
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }
              }}
            >
              {/* Header */}
              <div style={{
                padding: '16px',
                borderBottom: '1px solid #E5E7EB',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {post.platform === 'gmail' && <Mail size={18} color="#EA4335" />}
                  {post.platform === 'x' && <XIcon size={18} color="#000000" />}
                  {post.platform === 'youtube' && <PlayCircle size={18} color="#FF0000" />}
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                    {post.platform === 'gmail' ? 'Gmail' : post.platform === 'x' ? 'X' : 'YouTube'}
                  </span>

                  {/* Gmail Thread Indicator with Unread Badge */}
                  {post.platform === 'gmail' && post.emailData?.threadId && threadData[post.emailData.threadId] && (() => {
                    const threadInfo = threadData[post.emailData.threadId];
                    const threadHasMultipleMessages = threadInfo.threadDetails?.messages && threadInfo.threadDetails.messages.length > 1;

                    if (threadHasMultipleMessages) {
                      const hasUnreadMessages = threadInfo.threadDetails?.messages?.some((msg: any) => !msg.is_read) || threadInfo.unreadCount > 0;
                      const actualUnreadCount = threadInfo.unreadCount > 0 ? threadInfo.unreadCount :
                        (threadInfo.threadDetails?.messages?.filter((msg: any) => !msg.is_read).length || 0);

                      return (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          position: 'relative'
                        }}>
                          <MessageCircle size={14} color="#EA4335" />
                          {actualUnreadCount > 0 && (
                            <div style={{
                              background: '#EA4335',
                              color: 'white',
                              fontSize: '10px',
                              fontWeight: '600',
                              padding: '2px 6px',
                              borderRadius: '10px',
                              minWidth: '18px',
                              textAlign: 'center'
                            }}>
                              {actualUnreadCount}
                            </div>
                          )}
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Calendar size={14} color="#6B7280" />
                      <span style={{ fontSize: '12px', color: '#6B7280' }}>
                        {new Date(post.date).toLocaleDateString()}
                      </span>
                    </div>
                    {/* Show time for Gmail posts */}
                    {post.platform === 'gmail' && (
                      <span style={{ fontSize: '11px', color: '#9CA3AF' }}>
                        {new Date(post.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Content */}
              <div style={{ padding: '16px' }}>
                {/* YouTube Embed */}
                {post.platform === 'youtube' && post.provider_post_id && (
                  <div style={{
                    position: 'relative',
                    paddingBottom: '56.25%',
                    height: 0,
                    overflow: 'hidden',
                    borderRadius: '8px',
                    marginBottom: '12px',
                    background: '#000'
                  }}>
                    <iframe
                      src={`https://www.youtube.com/embed/${post.provider_post_id}`}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        border: 'none'
                      }}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                )}

                {/* Gmail-specific content */}
                {post.platform === 'gmail' && post.emailData && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {/* To field */}
                    {post.emailData.to && post.emailData.to.length > 0 && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '8px',
                        padding: '8px',
                        background: '#F9FAFB',
                        borderRadius: '6px'
                      }}>
                        <span style={{
                          fontSize: '13px',
                          fontWeight: '600',
                          color: '#6B7280',
                          minWidth: '28px'
                        }}>
                          To:
                        </span>
                        <span style={{
                          fontSize: '13px',
                          color: '#374151',
                          lineHeight: '1.5'
                        }}>
                          {Array.isArray(post.emailData.to)
                            ? post.emailData.to.join(', ')
                            : post.emailData.to}
                        </span>
                      </div>
                    )}
                    
                    {/* Subject */}
                    {post.emailData.subject && (
                      <h3 style={{
                        fontSize: '15px',
                        fontWeight: '600',
                        color: '#151133',
                        margin: '4px 0',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {post.emailData.subject}
                      </h3>
                    )}
                    
                    {/* Snippet */}
                    {post.emailData.snippet && (
                      <p style={{
                        fontSize: '14px',
                        color: '#6B7280',
                        margin: '0 0 12px 0',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {post.emailData.snippet}
                      </p>
                    )}

                    {/* Attachment Previews */}
                    {post.emailData.attachments && post.emailData.attachments.length > 0 && (
                      <div style={{
                        marginTop: '12px',
                        padding: '12px',
                        backgroundColor: '#F9FAFB',
                        borderRadius: '8px',
                        border: '1px solid #E5E7EB'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          marginBottom: '8px'
                        }}>
                          <Paperclip size={14} color="#6B7280" />
                          <span style={{
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#6B7280'
                          }}>
                            {post.emailData.attachments.length} Attachment{post.emailData.attachments.length > 1 ? 's' : ''}
                          </span>
                        </div>

                        {/* Image/Video Preview Grid */}
                        {post.emailData?.attachments && post.emailData.attachments.some((att: any) => {
                          const type = att.mimeType || att.content_type || att.type || '';
                          return type.startsWith('image/') || type.startsWith('video/');
                        }) && (
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: post.emailData.attachments.filter((att: any) => {
                              const type = att.mimeType || att.content_type || att.type || '';
                              return type.startsWith('image/') || type.startsWith('video/');
                            }).length === 1 ? '1fr' : 'repeat(2, 1fr)',
                            gap: '8px',
                            marginBottom: '12px',
                            borderRadius: '8px',
                            overflow: 'hidden'
                          }}>
                            {post.emailData.attachments
                              .filter((att: any) => {
                                const type = att.mimeType || att.content_type || att.type || '';
                                return type.startsWith('image/') || type.startsWith('video/');
                              })
                              .slice(0, 4)
                              .map((attachment: any, idx: number) => {
                                const type = attachment.mimeType || attachment.content_type || attachment.type || '';
                                const isVideo = type.startsWith('video/');
                                const imageCount = post.emailData!.attachments!.filter((att: any) => {
                                  const t = att.mimeType || att.content_type || att.type || '';
                                  return t.startsWith('image/') || t.startsWith('video/');
                                }).length;
                                return (
                                  <div
                                    key={idx}
                                    style={{
                                      position: 'relative',
                                      paddingBottom: imageCount === 1 ? '56.25%' : '100%',
                                      backgroundColor: '#F3F4F6',
                                      borderRadius: '6px',
                                      overflow: 'hidden'
                                    }}
                                  >
                                    {isVideo ? (
                                      <div
                                        style={{
                                          position: 'absolute',
                                          top: 0,
                                          left: 0,
                                          width: '100%',
                                          height: '100%',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          backgroundColor: '#F3F4F6'
                                        }}
                                      >
                                        <Video size={24} color="#6B7280" />
                                      </div>
                                    ) : (
                                      <>
                                        <img
                                          src={attachment.url}
                                          alt={attachment.filename}
                                          style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover'
                                          }}
                                          onError={(e) => {
                                            e.currentTarget.style.display = 'none';
                                            const placeholder = e.currentTarget.nextElementSibling as HTMLElement;
                                            if (placeholder) placeholder.style.display = 'flex';
                                          }}
                                        />
                                        <div
                                          style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%',
                                            display: 'none',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: '#F3F4F6'
                                          }}
                                        >
                                          <Image size={24} color="#6B7280" />
                                        </div>
                                      </>
                                    )}
                                  </div>
                                );
                              })}
                          </div>
                        )}

                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '6px'
                        }}>
                          {post.emailData.attachments.slice(0, 3).map((attachment, idx) => (
                            <div
                              key={idx}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '6px 8px',
                                backgroundColor: 'white',
                                borderRadius: '6px',
                                border: '1px solid #E5E7EB'
                              }}
                            >
                              <div style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '6px',
                                backgroundColor: '#F3F4F6',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                              }}>
                                {(() => {
                                  const type = (attachment as any).mimeType || (attachment as any).content_type || (attachment as any).type || '';
                                  if (type.startsWith('image/')) return <Image size={16} color="#6B7280" />;
                                  if (type.startsWith('video/')) return <Video size={16} color="#6B7280" />;
                                  if (type.includes('pdf')) return <FileText size={16} color="#6B7280" />;
                                  return <Paperclip size={16} color="#6B7280" />;
                                })()}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{
                                  fontSize: '13px',
                                  color: '#374151',
                                  fontWeight: '500',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }}>
                                  {attachment.filename}
                                </div>
                                {attachment.size && (
                                  <div style={{
                                    fontSize: '11px',
                                    color: '#9CA3AF'
                                  }}>
                                    {(attachment.size / 1024).toFixed(1)} KB
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                          {post.emailData.attachments.length > 3 && (
                            <div style={{
                              fontSize: '12px',
                              color: '#6B7280',
                              textAlign: 'center',
                              padding: '4px'
                            }}>
                              +{post.emailData.attachments.length - 3} more
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Regular post content for non-Gmail */}
                {post.platform !== 'gmail' && (
                  <>
                    {post.title && (
                      <h3 style={{
                        fontSize: '15px',
                        fontWeight: '600',
                        color: '#151133',
                        margin: '0 0 8px 0',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {post.title}
                      </h3>
                    )}

                    {post.text && (
                      <p style={{
                        fontSize: '14px',
                        color: '#6B7280',
                        margin: post.platform === 'x' && post.media_urls?.length ? '0 0 12px 0' : 0,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {post.text}
                      </p>
                    )}

                    {/* X Media Display */}
                    {post.platform === 'x' && post.media_urls && post.media_urls.length > 0 && (
                      <div style={{
                        marginTop: '12px',
                        display: 'grid',
                        gridTemplateColumns: post.media_urls.length === 1 ? '1fr' : 'repeat(2, 1fr)',
                        gap: '8px',
                        borderRadius: '12px',
                        overflow: 'hidden'
                      }}>
                        {post.media_urls.slice(0, 4).map((url, idx) => (
                          <div
                            key={idx}
                            style={{
                              position: 'relative',
                              paddingBottom: post.media_urls!.length === 1 ? '56.25%' : '100%',
                              backgroundColor: '#F3F4F6',
                              borderRadius: '8px',
                              overflow: 'hidden'
                            }}
                          >
                            <img
                              src={url}
                              alt={`Media ${idx + 1}`}
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                              onError={(e) => {
                                // Show placeholder if image fails to load
                                e.currentTarget.style.display = 'none';
                                const placeholder = e.currentTarget.nextElementSibling as HTMLElement;
                                if (placeholder) placeholder.style.display = 'flex';
                              }}
                            />
                            <div
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                display: 'none',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#F3F4F6'
                              }}
                            >
                              <XIcon size={24} color="#000000" />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* X Media Placeholder when no media */}
                    {post.platform === 'x' && (!post.media_urls || post.media_urls.length === 0) && post.text && (
                      <div style={{
                        marginTop: '12px',
                        padding: '32px',
                        backgroundColor: '#F3F4F6',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <XIcon size={32} color="#000000" style={{ opacity: 0.5 }} />
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Metrics Footer */}
              {(post.metrics || (post.media_urls && post.media_urls.length > 0)) && (
                <div style={{
                  padding: '12px 16px',
                  borderTop: '1px solid #E5E7EB',
                  display: 'flex',
                  gap: '16px',
                  flexWrap: 'wrap'
                }}>
                  {post.metrics?.views !== undefined && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Eye size={14} color="#6B7280" />
                      <span style={{ fontSize: '13px', color: '#6B7280' }}>
                        {post.metrics.views >= 1000 
                          ? `${(post.metrics.views / 1000).toFixed(1)}K` 
                          : post.metrics.views}
                      </span>
                    </div>
                  )}
                  {post.metrics?.likes !== undefined && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Heart size={14} color="#6B7280" />
                      <span style={{ fontSize: '13px', color: '#6B7280' }}>
                        {post.metrics.likes >= 1000 
                          ? `${(post.metrics.likes / 1000).toFixed(1)}K` 
                          : post.metrics.likes}
                      </span>
                    </div>
                  )}
                  {post.metrics?.comments !== undefined && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <MessageCircle size={14} color="#6B7280" />
                      <span style={{ fontSize: '13px', color: '#6B7280' }}>
                        {post.metrics.comments}
                      </span>
                    </div>
                  )}
                  {post.metrics?.retweets !== undefined && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Repeat size={14} color="#6B7280" />
                      <span style={{ fontSize: '13px', color: '#6B7280' }}>
                        {post.metrics.retweets}
                      </span>
                    </div>
                  )}
                  {post.metrics?.shares !== undefined && post.platform === 'youtube' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Share2 size={14} color="#6B7280" />
                      <span style={{ fontSize: '13px', color: '#6B7280' }}>
                        {post.metrics.shares || 0}
                      </span>
                    </div>
                  )}
                  {/* Attachment count indicator - only for non-YouTube */}
                  {post.media_urls && post.media_urls.length > 0 && post.platform !== 'youtube' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Paperclip size={14} color="#6B7280" />
                      <span style={{ fontSize: '13px', color: '#6B7280' }}>
                        {post.media_urls.length}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </Masonry>

        {/* Load more sent-email history (emails are paged 20 at a time) */}
        {hasMoreGmail && (activeFilter === 'all' || activeFilter === 'gmail') && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
            <button
              onClick={handleLoadMoreGmail}
              disabled={isLoadingMoreGmail}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '10px 20px',
                borderRadius: '8px',
                border: '1px solid #F9550D',
                background: '#FFF7ED',
                color: '#F9550D',
                fontSize: '14px',
                fontWeight: 600,
                fontFamily: 'Manrope, sans-serif',
                cursor: isLoadingMoreGmail ? 'default' : 'pointer',
                opacity: isLoadingMoreGmail ? 0.6 : 1,
              }}
            >
              <ChevronDown size={16} />
              {isLoadingMoreGmail ? 'Loading…' : 'Load more emails'}
            </button>
          </div>
        )}
        </div>
      ) : schoolCoachFilter ? (
        /* audit #211: results exist but the active filter matches nothing — this is a
           filtered-empty state, NOT a "connect your accounts" state. Say so and let
           the user clear the filter. */
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '80px 24px',
          textAlign: 'center',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #E5E7EB'
        }}>
          <Filter size={48} color="#D1D5DB" style={{ marginBottom: '16px' }} />
          <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', margin: '0 0 8px 0' }}>
            No posts match this filter
          </h3>
          <p style={{ fontSize: '14px', color: '#6B7280', margin: '0 0 16px 0' }}>
            Nothing found for {schoolCoachFilter.label}.
          </p>
          <button
            onClick={() => setSchoolCoachFilter(null)}
            style={{
              padding: '10px 20px',
              background: '#F9550D',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              fontFamily: 'Manrope, sans-serif'
            }}
          >
            Clear filter
          </button>
        </div>
      ) : (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '80px 24px',
          textAlign: 'center',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #E5E7EB'
        }}>
          <MessageCircle size={48} color="#D1D5DB" style={{ marginBottom: '16px' }} />
          <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', margin: '0 0 8px 0' }}>
            No posts yet
          </h3>
          <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>
            Connect social media accounts to start tracking posts
          </p>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .masonry-grid {
          display: flex;
          margin-left: -20px;
          width: auto;
        }

        .masonry-grid-column {
          padding-left: 20px;
          background-clip: padding-box;
        }
      `}</style>
      </div>

      {/* Gmail Detail Modal */}
      <GmailDetailModal
        visible={gmailModalVisible}
        onClose={() => {
          setGmailModalVisible(false);
          setSelectedGmailMessage(null);
        }}
        message={selectedGmailMessage}
        athleteId={athleteId}
        onThreadUpdate={(updatedMessage) => {
          setSelectedGmailMessage(updatedMessage);
          // Optionally reload posts to reflect updates
          if (athleteId) {
            loadSocialPosts(athleteId);
          }
        }}
      />

      {/* DM Coach Modal */}
      {selectedDMCoach && (
        <DMCoachModal
          isOpen={dmCoachModalOpen}
          onClose={() => {
            setDmCoachModalOpen(false);
            setSelectedDMCoach(null);
            // Refresh DM threads after closing
            if (athleteId) {
              loadDMThreads(athleteId);
            }
          }}
          coach={selectedDMCoach}
        />
      )}

      {/* Send a DM picker (APS-253). The DM window opens over it. */}
      {dmTargets && athleteId && (
        <Modal
          isOpen={sendDMOpen}
          onClose={() => setSendDMOpen(false)}
          ariaLabel="Send a DM"
          zIndex={1000}
          disableClose={sendDMComposerOpen}
          contentStyle={{
            width: isMobile ? 'calc(100vw - 32px)' : '100%',
            maxWidth: isMobile ? 'calc(100vw - 32px)' : '560px',
            maxHeight: isMobile ? 'calc(100vh - 100px)' : '85vh',
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 12px 0', flexShrink: 0 }}>
            <button
              onClick={() => setSendDMOpen(false)}
              aria-label="Close"
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer', padding: '8px',
                minWidth: '44px', minHeight: '44px', display: 'flex', alignItems: 'center',
                justifyContent: 'center', color: '#6B7280',
              }}
            >
              <X size={22} aria-hidden />
            </button>
          </div>
          <div style={{ padding: isMobile ? '0 16px 20px' : '0 24px 24px', overflowY: 'auto', flex: 1, minHeight: 0 }}>
            <SendDMPicker
              athleteId={athleteId}
              targets={dmTargets}
              onComposerOpenChange={(open) => {
                setSendDMComposerOpen(open);
                if (!open) loadDMThreads(athleteId);
              }}
              onSent={() => setSendDMOpen(false)}
            />
          </div>
        </Modal>
      )}

      {/* Standalone "Post to X" — no coach props => generic composer + tag lists */}
      <TweetCoachModal
        isOpen={postToXOpen}
        onClose={() => {
          setPostToXOpen(false);
          if (athleteId) loadSocialPosts(athleteId);
        }}
      />
    </AppLayout>
  );
};

export default Messages;
